import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Factory,
  Zap,
  Shield,
  CheckCircle,
  Clock,
  AlertTriangle,
  Users,
  TrendingUp
} from "lucide-react";
import { SectionHeader } from "../components/SectionHeader";
import { CTABand } from "../components/CTABand";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BookDemoModal } from "../components/BookDemoModal";

// Industry data
const industries = [
  {
    icon: <Zap className="h-8 w-8" />,
    title: "Energy & Utilities",
    description: "ISO 9001/14001/45001, NGER & ESG reporting",
    challenges: [
      "Constant regulation changes & audit pressure"
    ],
    benefits: [
      "Auto-updates, audit-ready docs, faster compliance cycles"
    ]
  },
  {
    icon: <Factory className="h-8 w-8" />,
    title: "Manufacturing / Construction",
    description: "ISO, WHS & Chain of Responsibility obligations",
    challenges: [
      "Costly recerts & tender compliance risk"
    ],
    benefits: [
      "Keeps policies current, ensures tender-readiness, saves 20%+ admin time"
    ]
  },
  {
    icon: <Building2 className="h-8 w-8" />,
    title: "Building & Industrial Services",
    description: "ISO, labour law & WHS compliance across sites",
    challenges: [
      "High staff turnover & manual updates"
    ],
    benefits: [
      "Up-to-date SOPs, scale across sites, reduce consultant costs"
    ]
  },
  {
    icon: <Users className="h-8 w-8" />,
    title: "Logistics / Transport",
    description: "Chain of Responsibility, fatigue & hazmat regs",
    challenges: [
      "Inconsistent docs across fleets & depots"
    ],
    benefits: [
      "Auto CoR updates, single source of truth, audit-ready registers"
    ]
  }
];

// Value propositions
const valueProps = [
  {
    icon: <Clock className="h-6 w-6" />,
    title: "Reduce Review Time by 80%",
    description: ""
  },
  {
    icon: <Shield className="h-6 w-6" />,
    title: "Eliminate Compliance Gaps",
    description: ""
  },
  {
    icon: <TrendingUp className="h-6 w-6" />,
    title: "Scale Without Compromise",
    description: ""
  },
  {
    icon: <CheckCircle className="h-6 w-6" />,
    title: "Ensure Audit Readiness",
    description: ""
  }
];

export default function IndustriesPage() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#0D2B22", color: "#FFFFFF" }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#0D2B22" }}>
        <div className="mx-auto max-w-7xl container-padding">
          <div className="py-16 sm:py-20 lg:py-24">
            <div className="text-center max-w-4xl mx-auto">
              <motion.h1
                className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl mb-6 font-merriweather"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                Industry-Specific Compliance Solutions
              </motion.h1>
              <motion.p
                className="text-base sm:text-lg md:text-xl text-[#9ABFB5] leading-relaxed mb-6 sm:mb-8 px-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
              >
                Revisr understands the unique compliance challenges across industries.
                Our AI-powered platform reduces risk, saves time, and ensures compliance confidence
                for HSEQ, Operations, and GRC teams.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
              >
                <button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  className="rounded-xl bg-[#2EB39A] px-8 py-3 font-bold text-[#0D2B22] hover:bg-[#25927E] transition-colors"
                >
                  Book a Demo
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications Section */}
      <section className="section-padding" style={{ backgroundColor: "#0D2B22" }}>
        <div className="mx-auto max-w-7xl container-padding">
          <SectionHeader
            badge="Industry Applications"
            title="Tailored solutions for your industry"
            subtitle="See how Revisr addresses the specific compliance challenges in your sector with industry-focused features and standards."
            className="mb-8"
          />

          {/* CTA Button below section header */}
          <div className="text-center mb-16">
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              className="rounded-xl bg-[#2EB39A] px-8 py-3 font-bold text-[#0D2B22] hover:bg-[#25927E] transition-colors"
            >
              Book a Demo
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                className="rounded-2xl shadow-lg border border-[#1A4035] p-8 hover:shadow-xl transition-shadow duration-300"
                style={{ backgroundColor: "#0F3328" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.1 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center text-[#2EB39A] mr-4 shadow-lg" style={{ backgroundColor: "#1A4035" }}>
                    {industry.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-white">{industry.title}</h3>
                </div>

                <p className="text-[#9ABFB5] mb-6 leading-relaxed">
                  {industry.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-semibold text-red-400 uppercase tracking-wide mb-3 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Current Challenges
                    </h4>
                    <ul className="space-y-2">
                      {industry.challenges.map((challenge, idx) => (
                        <li key={idx} className="text-sm text-[#9ABFB5] flex items-start">
                          <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          {challenge}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-[#2EB39A] uppercase tracking-wide mb-3 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Revisr Benefits
                    </h4>
                    <ul className="space-y-2">
                      {industry.benefits.map((benefit, idx) => (
                        <li key={idx} className="text-sm text-[#9ABFB5] flex items-start">
                          <span className="w-1.5 h-1.5 bg-[#2EB39A] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Success Stories Section */}
      <section className="section-padding" style={{ backgroundColor: "#0D2B22" }}>
        <div className="mx-auto max-w-7xl container-padding">
          <SectionHeader
            badge="Success Stories"
            title="Real results from real teams"
            subtitle="See how compliance teams across industries are transforming their workflows with Revisr."
            className="mb-16"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="rounded-2xl p-8 border border-[#1A4035]" style={{ backgroundColor: "#0F3328" }}>
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-[#0D2B22] mr-4" style={{ backgroundColor: "#2EB39A" }}>
                  <Factory className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Manufacturing Company</h4>
                  <p className="text-sm text-[#9ABFB5]">Fortune 500</p>
                </div>
              </div>
              <p className="text-[#9ABFB5] mb-4">
                "Revisr reduced our ISO compliance review time from 8 hours to 45 minutes.
                We can now handle 3x more documents with the same team."
              </p>
              <div className="text-2xl font-bold text-[#2EB39A]">85% Time Saved</div>
            </div>

            <div className="rounded-2xl p-8 border border-[#1A4035]" style={{ backgroundColor: "#0F3328" }}>
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-[#0D2B22] mr-4" style={{ backgroundColor: "#2EB39A" }}>
                  <Building2 className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Construction Firm</h4>
                  <p className="text-sm text-[#9ABFB5]">Mid-size Enterprise</p>
                </div>
              </div>
              <p className="text-[#9ABFB5] mb-4">
                "Our safety compliance documentation is now always audit-ready.
                Revisr caught 12 potential issues before our last safety audit."
              </p>
              <div className="text-2xl font-bold text-[#2EB39A]">100% Audit Ready</div>
            </div>

            <div className="rounded-2xl p-8 border border-[#1A4035]" style={{ backgroundColor: "#0F3328" }}>
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-[#0D2B22] mr-4" style={{ backgroundColor: "#2EB39A" }}>
                  <Users className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Corporate GRC Team</h4>
                  <p className="text-sm text-[#9ABFB5]">Global Corporation</p>
                </div>
              </div>
              <p className="text-[#9ABFB5] mb-4">
                "Revisr unified our compliance across 15 business units.
                Policy alignment is now consistent and automated."
              </p>
              <div className="text-2xl font-bold text-[#2EB39A]">15 Units Unified</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="early-access">
        <CTABand
          title="Ready to transform your compliance process?"
          description="Join now and get 10% off"
          onOpenDemo={() => setDemoOpen(true)}
        />
      </section>

      <Footer />

      <BookDemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
